/**
 * 🎬 剧组积分管理弹窗
 *
 * 制片人专用：查看所有成员的积分余额和已加给的净额，可加积分/扣回积分。
 *
 * 核心逻辑：
 * - 加积分：从制片人扣除，零和转账
 * - 扣回：净额上限 = 已加给的总数，余额上限 = 成员当前余额，取最小值
 * - 每个操作支持输入理由（可选）
 */
import React, { useState, useEffect } from 'react';
import api from '../api';

function CrewCreditManageModal({ open, crewId, onClose, onCreditsChange }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [operating, setOperating] = useState(false);
  const [alertMsg, setAlertMsg] = useState('');
  const [producerCredits, setProducerCredits] = useState(0);

  useEffect(() => {
    if (!open || !crewId) return;
    loadMembers();
    loadProducerCredits();
  }, [open, crewId]);

  const loadProducerCredits = async () => {
    try {
      const res = await api.get('/api/auth/me');
      console.log('[DEBUG] 获取制片人信息返回:', res);
      // /api/auth/me 返回 {user: {...}} 结构
      const user = res.user || res;
      setProducerCredits(user.credits || 0);
    } catch (err) {
      console.error('获取制片人积分失败:', err);
    }
  };

  const loadMembers = async () => {
    setLoading(true);
    try {
      const data = await api.get(`/api/crews/${crewId}/credits/members`);
      console.log('[DEBUG] 获取成员列表返回:', data);
      setMembers(data.members || []);
    } catch (err) {
      console.error('获取成员列表失败:', err);
      setAlertMsg('加载失败');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  // 加积分
  const handleGive = async (memberId, amount, reason) => {
    console.log('[DEBUG] handleGive 被调用:', { memberId, amount, reason });
    if (amount <= 0 || !Number.isInteger(amount)) {
      setAlertMsg('请输入有效的正整数');
      return;
    }
    if (amount > producerCredits) {
      setAlertMsg(`您的积分余额不足（当前 ${producerCredits}）`);
      return;
    }

    setOperating(true);
    try {
      console.log('[DEBUG] 发送加积分请求:', { crewId, member_id: memberId, amount, reason });
      const result = await api.post(`/api/crews/${crewId}/credits/give`, {
        member_id: memberId,
        amount,
        reason
      });
      console.log('[DEBUG] 加积分成功，返回:', result);
      setAlertMsg('加积分成功');
      await loadMembers();
      await loadProducerCredits();
      onCreditsChange?.();
    } catch (err) {
      console.error('[DEBUG] 加积分失败:', err);
      setAlertMsg(err.response?.data?.error || '操作失败');
    } finally {
      setOperating(false);
    }
  };

  // 扣积分
  const handleRevoke = async (memberId, amount, givenAmount, reason) => {
    if (amount <= 0 || !Number.isInteger(amount)) {
      setAlertMsg('请输入有效的正整数');
      return;
    }
    if (amount > givenAmount) {
      setAlertMsg(`扣除失败：您只给该成员加过 ${givenAmount} 积分`);
      return;
    }

    setOperating(true);
    try {
      await api.post(`/api/crews/${crewId}/credits/revoke`, {
        member_id: memberId,
        amount,
        reason
      });
      setAlertMsg('扣回成功');
      await loadMembers();
      await loadProducerCredits();
      onCreditsChange?.();
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || '操作失败';
      setAlertMsg(msg);
    } finally {
      setOperating(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: 20
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#0F131C',
          borderRadius: 12,
          border: '1px solid rgba(255,255,255,0.1)',
          width: '100%',
          maxWidth: 600,
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* 标题栏 */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'rgba(59,130,246,0.05)'
        }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#fff' }}>
              团队积分管理
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginTop: 4 }}>
              您的余额: {producerCredits} 积分
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'rgba(255,255,255,0.6)',
              cursor: 'pointer',
              fontSize: 24,
              lineHeight: 1,
              padding: 4
            }}
          >
            ×
          </button>
        </div>

        {/* 成员列表 */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>
              加载中...
            </div>
          ) : members.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>
              暂无成员
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {members.map(member => (
                <MemberCreditRow
                  key={member.user_id}
                  member={member}
                  onGive={handleGive}
                  onRevoke={handleRevoke}
                  operating={operating}
                />
              ))}
            </div>
          )}
        </div>

        {/* 提示信息 */}
        {alertMsg && (
          <div style={{
            padding: '12px 20px',
            background: 'var(--bg-secondary)',
            borderTop: '1px solid var(--border-subtle)',
            color: 'var(--text-primary)',
            fontSize: 13
          }}>
            {alertMsg}
          </div>
        )}
      </div>
    </div>
  );
}

// 单个成员行组件
function MemberCreditRow({ member, onGive, onRevoke, operating }) {
  const [showGiveInput, setShowGiveInput] = useState(false);
  const [showRevokeInput, setShowRevokeInput] = useState(false);
  const [giveAmount, setGiveAmount] = useState('');
  const [revokeAmount, setRevokeAmount] = useState('');
  const [giveReason, setGiveReason] = useState('');
  const [revokeReason, setRevokeReason] = useState('');

  const handleGiveSubmit = () => {
    const amount = parseInt(giveAmount, 10);
    if (!isNaN(amount) && amount > 0) {
      onGive(member.user_id, amount, giveReason || '制片人转入');
      setGiveAmount('');
      setGiveReason('');
      setShowGiveInput(false);
    }
  };

  const handleRevokeSubmit = () => {
    const amount = parseInt(revokeAmount, 10);
    if (!isNaN(amount) && amount > 0) {
      // 前置校验：不能超过 maxRevoke
      if (amount > maxRevoke) {
        // 不关闭输入框，让父组件显示错误提示
        onRevoke(member.user_id, amount, member.given_amount, revokeReason || '制片人撤回');
        return; // 不清空输入，不关闭
      }
      onRevoke(member.user_id, amount, member.given_amount, revokeReason || '制片人撤回');
      setRevokeAmount('');
      setRevokeReason('');
      setShowRevokeInput(false);
    }
  };

  // 计算扣回上限：min(成员余额, 已加给净额)
  const maxRevoke = Math.min(member.balance, member.given_amount);

  return (
    <div style={{
      padding: 16,
      background: 'var(--bg-secondary)',
      borderRadius: 8,
      border: '1px solid var(--border-subtle)'
    }}>
      {/* 成员信息行 */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: showGiveInput || showRevokeInput ? 12 : 0
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>
            {member.username}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
            余额: {member.balance} 积分 · 已加给净额: {member.given_amount}
          </div>
        </div>

        {/* 操作按钮 */}
        {!showGiveInput && !showRevokeInput && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => setShowGiveInput(true)}
              disabled={operating}
              style={{
                padding: '6px 16px',
                background: '#22c55e',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: operating ? 'not-allowed' : 'pointer',
                fontSize: 12,
                fontWeight: 500,
                opacity: operating ? 0.5 : 1
              }}
            >
              加积分
            </button>
            <button
              onClick={() => setShowRevokeInput(true)}
              disabled={operating || member.given_amount === 0}
              style={{
                padding: '6px 16px',
                background: member.given_amount === 0 ? 'var(--bg-hover)' : '#ef4444',
                color: member.given_amount === 0 ? 'var(--text-secondary)' : '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: (operating || member.given_amount === 0) ? 'not-allowed' : 'pointer',
                fontSize: 12,
                fontWeight: 500,
                opacity: operating ? 0.5 : 1
              }}
            >
              扣回
            </button>
          </div>
        )}
      </div>

      {/* 加积分输入框 */}
      {showGiveInput && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <input
            type="number"
            value={giveAmount}
            onChange={e => setGiveAmount(e.target.value)}
            placeholder="输入积分数量"
            autoFocus
            style={{
              padding: '6px 10px',
              background: 'var(--bg-primary)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 6,
              color: 'var(--text-primary)',
              fontSize: 13
            }}
            onKeyPress={e => { if (e.key === 'Enter' && !e.shiftKey) handleGiveSubmit(); }}
          />
          <input
            type="text"
            value={giveReason}
            onChange={e => setGiveReason(e.target.value)}
            placeholder="备注理由（可选）"
            style={{
              padding: '6px 10px',
              background: 'var(--bg-primary)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 6,
              color: 'var(--text-primary)',
              fontSize: 13
            }}
            onKeyPress={e => { if (e.key === 'Enter' && !e.shiftKey) handleGiveSubmit(); }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={handleGiveSubmit}
              disabled={operating || !giveAmount}
              style={{
                flex: 1,
                padding: '6px 16px',
                background: '#22c55e',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: (operating || !giveAmount) ? 'not-allowed' : 'pointer',
                fontSize: 12,
                fontWeight: 500,
                opacity: (operating || !giveAmount) ? 0.5 : 1
              }}
            >
              确定
            </button>
            <button
              onClick={() => { setShowGiveInput(false); setGiveAmount(''); setGiveReason(''); }}
              disabled={operating}
              style={{
                flex: 1,
                padding: '6px 16px',
                background: 'var(--bg-hover)',
                color: 'var(--text-secondary)',
                border: 'none',
                borderRadius: 6,
                cursor: operating ? 'not-allowed' : 'pointer',
                fontSize: 12
              }}
            >
              取消
            </button>
          </div>
        </div>
      )}

      {/* 扣回输入框 */}
      {showRevokeInput && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: -4 }}>
            最多可扣回: {maxRevoke} 积分（成员余额 {member.balance}，已加给 {member.given_amount}）
          </div>
          <input
            type="number"
            value={revokeAmount}
            onChange={e => setRevokeAmount(e.target.value)}
            placeholder={`输入扣回数量（≤${maxRevoke}）`}
            autoFocus
            style={{
              padding: '6px 10px',
              background: 'var(--bg-primary)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 6,
              color: 'var(--text-primary)',
              fontSize: 13
            }}
            onKeyPress={e => { if (e.key === 'Enter' && !e.shiftKey) handleRevokeSubmit(); }}
          />
          <input
            type="text"
            value={revokeReason}
            onChange={e => setRevokeReason(e.target.value)}
            placeholder="扣回理由（可选）"
            style={{
              padding: '6px 10px',
              background: 'var(--bg-primary)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 6,
              color: 'var(--text-primary)',
              fontSize: 13
            }}
            onKeyPress={e => { if (e.key === 'Enter' && !e.shiftKey) handleRevokeSubmit(); }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={handleRevokeSubmit}
              disabled={operating || !revokeAmount}
              style={{
                flex: 1,
                padding: '6px 16px',
                background: '#ef4444',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: (operating || !revokeAmount) ? 'not-allowed' : 'pointer',
                fontSize: 12,
                fontWeight: 500,
                opacity: (operating || !revokeAmount) ? 0.5 : 1
              }}
            >
              确定
            </button>
            <button
              onClick={() => { setShowRevokeInput(false); setRevokeAmount(''); setRevokeReason(''); }}
              disabled={operating}
              style={{
                flex: 1,
                padding: '6px 16px',
                background: 'var(--bg-hover)',
                color: 'var(--text-secondary)',
                border: 'none',
                borderRadius: 6,
                cursor: operating ? 'not-allowed' : 'pointer',
                fontSize: 12
              }}
            >
              取消
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CrewCreditManageModal;
